import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Loader2, ClipboardList, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, isPast, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_CHECKLISTS = [
  { id: '1', name: 'Daily Site Inspection', due_date: new Date().toISOString(), completed_items: 3, total_items: 10, project_name: 'Site Alpha' },
  { id: '2', name: 'Weekly Safety Check', due_date: new Date(Date.now() + 86400000).toISOString(), completed_items: 0, total_items: 15, project_name: 'Main Office' },
  { id: '3', name: 'Equipment Maintenance', due_date: new Date(Date.now() - 86400000).toISOString(), completed_items: 5, total_items: 8, project_name: 'Warehouse B' },
  { id: '4', name: 'Fire Safety Audit', due_date: new Date(Date.now() + 172800000).toISOString(), completed_items: 12, total_items: 20, project_name: 'Distribution Center' },
];

export function ChecklistsDueTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company, userProfile } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setChecklists(MOCK_CHECKLISTS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadChecklists = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('checklists')
          .select(`
            id,
            name,
            due_date,
            completed_items,
            total_items,
            projects!inner(name, company_id)
          `)
          .eq('projects.company_id', company.id)
          .eq('status', 'in_progress')
          .not('due_date', 'is', null)
          .order('due_date', { ascending: true })
          .limit(10);

        if (error) throw error;

        const formattedData = (data || []).map(c => ({
          id: c.id,
          name: c.name,
          due_date: c.due_date,
          completed_items: c.completed_items || 0,
          total_items: c.total_items || 0,
          project_name: c.projects?.name
        }));

        setChecklists(formattedData);
      } catch (error) {
        console.error('Failed to load checklists:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChecklists();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (checklists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-green-100 p-3 rounded-full mb-3">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-sm font-medium text-gray-900">All caught up!</p>
        <p className="text-xs text-gray-500 mt-1">No pending checklists</p>
      </div>
    );
  }

  const overdueCount = checklists.filter(c => c.due_date && isPast(parseISO(c.due_date))).length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-orange-500 text-white">{checklists.length}</Badge>
          <span className="text-xs text-gray-600">in progress</span>
        </div>
        {overdueCount > 0 && (
          <Badge variant="destructive" className="text-[10px]">
            {overdueCount} overdue
          </Badge>
        )}
      </div>

      {/* Checklists list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {checklists.map((checklist) => {
            const isOverdue = checklist.due_date && isPast(parseISO(checklist.due_date));
            const progress = checklist.total_items > 0
              ? Math.round((checklist.completed_items / checklist.total_items) * 100)
              : 0;

            return (
              <div
                key={checklist.id}
                onClick={() => navigate(`/checklist/${checklist.id}`)}
                className={cn(
                  "p-2 rounded-lg border cursor-pointer transition-all hover:shadow-md",
                  isOverdue ? "bg-red-50 border-red-200" : "bg-white"
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {checklist.name}
                    </p>
                    {checklist.project_name && (
                      <p className="text-[10px] text-gray-500 truncate">
                        {checklist.project_name}
                      </p>
                    )}
                  </div>
                  {isOverdue ? (
                    <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-gray-400 shrink-0" />
                  )}
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-2">
                  <Progress value={progress} className="h-1.5 flex-1" />
                  <span className="text-[10px] text-gray-600 shrink-0">
                    {checklist.completed_items}/{checklist.total_items}
                  </span>
                </div>

                {/* Due date */}
                <div className="flex items-center gap-1 mt-1">
                  <span className={cn(
                    "text-[10px]",
                    isOverdue ? "text-red-600 font-medium" : "text-gray-500"
                  )}>
                    {isOverdue ? 'Overdue: ' : 'Due: '}
                    {formatDistanceToNow(parseISO(checklist.due_date), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/checklists')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors shrink-0"
      >
        <ClipboardList className="h-3.5 w-3.5" />
        <span>View All Checklists</span>
      </button>
    </div>
  );
}
